import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Plus, Upload, UserPlus } from 'lucide-react';
import DashboardNavbar from '../dashboardNavbar';
import { Btn, Tag, TagDot, Field, Input, Select, FormGrid, SetTabs, SetTab } from '../ui/primitives';
import DataTable from '../ui/DataTable';
import PageActionsMenu, {
  ActionsMenuItem,
  ActionsSection,
} from '../ui/PageActionsMenu';
import { ModalShell } from '../ui/shell';
import { useAppContext } from '../../appprovider';
import { inviteOrgMember } from '../services/orgMemberInviteApi';
import { resendInvite, revokeInvite } from '../services/orgInviteApi';
import { resolveAdminUsersGuards, canListOrgMembers, isSelfOrgMember } from '../services/adminUsersAccess';
import { INVITE_TEAM_MIN_TIER } from '../services/instructorMeterLimits';
import {
  fetchOrgAdminUsers,
  fetchOrgMemberDetail,
  fetchOrgLearnerDetail,
  updateOrgInstructorActive,
  updateOrgLearnerActive,
} from '../services/adminUsersApi';
import {
  ADMIN_USERS_LIST_DEFAULTS,
  ADMIN_USERS_SORT_FIELDS,
  ADMIN_USER_KINDS,
  buildAdminUsersListCacheKey,
  formatAdminUserName,
  getNextAdminUsersSort,
  sortAdminUsersLocally,
} from '../services/adminUsersListApi';
import { getCache, setCache, clearCacheByPrefix } from '../ui/listCache';
import PackageDialog from '../course/packagedialog';
import AdminUserDetailSheet from './AdminUserDetailSheet';
import AdminUsersRowActionsMenu from './AdminUsersRowActionsMenu';
import { ADMIN_TABS } from './adminSectionTabs';
import { useConfirm } from '../ui/ConfirmProvider';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const PAGE_SIZE = 10;

const Shell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) { padding: 0 14px 28px; }
`;

const Hero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  h2 { margin: 0; color: ${({ theme }) => theme.text}; font-family: ${({ theme }) => theme.fontHead}; font-size: 16px; font-weight: 700; letter-spacing: -.01em; }
  p { margin: 2px 0 0; color: ${({ theme }) => theme.textMuted}; font-size: ${({ theme }) => theme.fsSm}; }
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Wrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const PlanHint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme, $tone }) => (
    $tone === 'plan'
      ? `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 28%, ${theme.border})`
      : theme.border
  )};
  background: ${({ theme, $tone }) => (
    $tone === 'plan'
      ? `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 8%, ${theme.surface})`
      : theme.surface
  )};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const ErrorNote = styled.div`
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.danger};
  line-height: 1.45;
`;

const SuccessNote = styled.div`
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.success || theme.accent};
  line-height: 1.45;
`;

const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const FileInput = styled.input`
  width: 100%;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
`;

const ApiNote = () => (
  <ErrorNote as="p" style={{ color: 'inherit', fontStyle: 'italic' }}>
    API wiring pending — this dialog is a UI placeholder.
  </ErrorNote>
);

export default function AdminUsers() {
  const { entitlements, openUpgradeGate } = useAppContext();
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const askConfirmation = useConfirm();
  const userId = localStorage.getItem('userId');
  const userType = localStorage.getItem('userType');
  const orgId = entitlements?.orgId;
  const canLoadMembers = canListOrgMembers(userType, entitlements);
  const guards = resolveAdminUsersGuards(entitlements);
  const {
    isAdmin,
    canManageTeam,
    canCreateUser,
    canInvite,
    canImportUsers,
    teamGateBody,
    roleBlockedMessage,
    planBlockedMessage,
    selfBlockedMessage,
  } = guards;

  // Create-user role options depend on the acting user's role: an owner can create
  // admins or instructors; an admin can only create instructors. Learner accounts are
  // provisioned through enrollment, not this dialog.
  const createRoleOptions =
    String(entitlements?.role || '').toLowerCase() === 'owner'
      ? ['Admin', 'Instructor']
      : ['Instructor'];

  const [userKind, setUserKind] = useState(ADMIN_USER_KINDS.instructor);
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createRole, setCreateRole] = useState('Instructor');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [listSuccess, setListSuccess] = useState('');
  const [showPlans, setShowPlans] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [userDialog, setUserDialog] = useState(null);
  const [listPage, setListPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [sortBy, setSortBy] = useState(ADMIN_USERS_LIST_DEFAULTS.sortBy);
  const [sortDir, setSortDir] = useState(ADMIN_USERS_LIST_DEFAULTS.sortDir);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detail, setDetail] = useState(null);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setListPage(0);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchKeyword]);

  const applyListPage = useCallback((parsed) => {
    setMembers(parsed.content);
    setTotalElements(parsed.totalElements);
    setTotalPages(Math.max(parsed.totalPages, 1));
  }, []);

  const loadMembers = useCallback(async () => {
    if (!userId || !orgId || !canLoadMembers) {
      setMembers([]);
      setTotalElements(0);
      setTotalPages(1);
      return;
    }

    const listParams = {
      kind: userKind,
      page: listPage,
      size: PAGE_SIZE,
      keyword: debouncedKeyword,
    };
    const cacheKey = buildAdminUsersListCacheKey('org', orgId, listParams);
    const cached = getCache(cacheKey);
    if (cached) {
      applyListPage(cached);
      setMembersLoading(false);
      return;
    }

    setMembersLoading(true);
    setMembersError('');
    try {
      const parsed = await fetchOrgAdminUsers({
        adminUserId: userId,
        orgId,
        ...listParams,
      });
      applyListPage(parsed);
      setCache(cacheKey, parsed);
    } catch (error) {
      setMembersError('Unable to load users right now.');
      setMembers([]);
      setTotalElements(0);
      setTotalPages(1);
    } finally {
      setMembersLoading(false);
    }
  }, [
    userId,
    orgId,
    canLoadMembers,
    userKind,
    listPage,
    debouncedKeyword,
    applyListPage,
  ]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const closeUserDialog = () => {
    setUserDialog(null);
    setCreateFirstName('');
    setCreateLastName('');
    setCreateEmail('');
    setCreateRole('Instructor');
    setCreateError('');
  };

  const openUserDialog = (mode) => {
    const demoKey = mode === 'create'
      ? 'action.admin.user.create'
      : 'action.admin.user.import';
    if (!guardAction(demoKey)) return;
    if (!isAdmin) {
      setCreateError(roleBlockedMessage);
      setUserDialog(null);
      return;
    }
    if (!canManageTeam) {
      openUpgradeGate(teamGateBody);
      return;
    }
    setCreateError('');
    setUserDialog(mode);
  };

  const guardTeamAction = () => {
    if (!isAdmin) {
      return roleBlockedMessage;
    }
    if (!canManageTeam) {
      openUpgradeGate(teamGateBody);
      return planBlockedMessage;
    }
    return null;
  };

  const promptTeamBlock = () => {
    if (!isAdmin) {
      askConfirmation({
        title: 'Access restricted',
        message: roleBlockedMessage,
        confirmLabel: 'OK',
        requireTypedConfirm: false,
      });
      return;
    }
    if (!canManageTeam) {
      openUpgradeGate(teamGateBody);
    }
  };

  const promptRoleBlock = () => {
    askConfirmation({
      title: 'Access restricted',
      message: roleBlockedMessage,
      confirmLabel: 'OK',
      requireTypedConfirm: false,
    });
  };

  const promptSelfBlock = () => {
    askConfirmation({
      title: 'Not available',
      message: selfBlockedMessage,
      confirmLabel: 'OK',
      requireTypedConfirm: false,
    });
  };

  // Staff hierarchy for changing a member's access: an owner may manage admins +
  // instructors; an admin may manage instructors only (never the owner or fellow admins).
  // Learner rows carry no staff role, so any org admin may manage them.
  const myOrgRole = String(entitlements?.role || '').toLowerCase();
  const canManageRowStatus = (row) => {
    if (!isAdmin || isSelfOrgMember(userId, row)) {
      return false;
    }
    const targetRole = String(row.role || '').toLowerCase();
    if (targetRole !== 'owner' && targetRole !== 'admin' && targetRole !== 'instructor') {
      return true; // learners / non-staff rows
    }
    return myOrgRole === 'owner'
      ? (targetRole === 'admin' || targetRole === 'instructor')
      : targetRole === 'instructor';
  };

  const promptRowBlock = (row) => {
    if (isSelfOrgMember(userId, row)) {
      promptSelfBlock();
      return;
    }
    const targetRole = String(row.role || '').toLowerCase();
    if (targetRole === 'owner') {
      askConfirmation({
        title: 'Not allowed',
        message: "You can't disable the access of the organisation owner.",
        confirmLabel: 'OK',
        requireTypedConfirm: false,
      });
      return;
    }
    if (targetRole === 'admin') {
      askConfirmation({
        title: 'Not allowed',
        message: "You can't disable the access of another admin — only the organisation owner can.",
        confirmLabel: 'OK',
        requireTypedConfirm: false,
      });
      return;
    }
    promptRoleBlock();
  };

  const isRowActionLocked = (row) => !canManageRowStatus(row);

  const teamActionsLocked = !isAdmin || !canManageTeam;
  const inviteDemoLocked = isLocked('action.admin.user.invite');
  const createDemoLocked = isLocked('action.admin.user.create');
  const importDemoLocked = isLocked('action.admin.user.import');

  const sendOrgInvite = async (email, { setLoading, setError, firstName, lastName, role }) => {
    setError('');
    setListSuccess('');

    if (!isAdmin) {
      setError(roleBlockedMessage);
      return;
    }
    if (!canInvite) {
      openUpgradeGate(teamGateBody);
      return;
    }
    if (!email.trim()) {
      setError('Enter an email address.');
      return;
    }
    if (!userId || !orgId) {
      setError('Organization context is missing. Refresh and try again.');
      return;
    }

    setLoading(true);
    try {
      const result = await inviteOrgMember({
        inviterUserId: userId,
        orgId,
        email,
        ...(firstName && firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName && lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(role ? { role: String(role).toLowerCase() } : {}),
      });
      if (result.gate) {
        openUpgradeGate(result.body);
        return;
      }
      if (result.ok) {
        setCreateEmail('');
        setCreateFirstName('');
        setCreateLastName('');
        closeUserDialog();
        clearCacheByPrefix(`admin-users:org:${orgId}:`);
        await loadMembers();
        setListSuccess('Invitation sent — pending until they accept.');
        return;
      }
      if (result.status === 404) {
        setError('No instructor account found for that email. They must sign up first.');
        return;
      }
      if (result.status === 409) {
        setError('User is already a member of this organization.');
        return;
      }
      setError(result.text || 'Unable to send invitation.');
    } catch (error) {
      setError('Unable to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!guardAction('action.admin.user.create')) return;
    setCreateError('');
    if (!createFirstName.trim()) {
      setCreateError('Enter a first name.');
      return;
    }
    await sendOrgInvite(createEmail, {
      setLoading: setCreateLoading,
      setError: setCreateError,
      firstName: createFirstName,
      lastName: createLastName,
      role: createRole,
    });
  };

  const handleKindChange = (kind) => {
    setUserKind(kind);
    setListPage(0);
  };

  const handlePageChange = (nextPage) => {
    setListPage(Math.max(nextPage - 1, 0));
  };

  const handleSortChange = (columnKey) => {
    const next = getNextAdminUsersSort(columnKey, sortBy, sortDir);
    setSortBy(next.sortBy);
    setSortDir(next.sortDir);
  };

  const handleRowClick = async (row) => {
    if (!userId || !orgId) {
      return;
    }
    // Pending invites have no linked account yet — no per-user detail to open.
    if (row.status === 'pending' || !row.userId) {
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetail(null);
    try {
      const payload = userKind === ADMIN_USER_KINDS.learner
        ? await fetchOrgLearnerDetail({
          adminUserId: userId,
          orgId,
          studentId: row.studentId || row.userId,
        })
        : await fetchOrgMemberDetail({
          adminUserId: userId,
          orgId,
          userId: row.userId,
        });
      setDetail(payload);
    } catch (error) {
      setDetailError('Unable to load user details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleStatus = async (row) => {
    if (!isAdmin || !userId || !orgId) {
      return;
    }
    if (!canManageRowStatus(row)) {
      promptRowBlock(row);
      return;
    }

    const targetId = row.userId || row.studentId;
    if (!targetId || targetId === userId) {
      return;
    }

    const isActive = row.active === 1;
    const actionLabel = isActive ? 'Disable access' : 'Enable access';
    const name = formatAdminUserName(row);

    const confirmed = await askConfirmation({
      title: `${actionLabel}?`,
      message: isActive
        ? `${name} will lose access to this organization until you enable them again.`
        : `${name} will regain access to this organization.`,
      confirmLabel: actionLabel,
      variant: isActive ? 'danger' : 'default',
      requireTypedConfirm: false,
    });
    if (!confirmed) {
      return;
    }

    try {
      if (userKind === ADMIN_USER_KINDS.learner) {
        await updateOrgLearnerActive({
          adminUserId: userId,
          orgId,
          studentId: row.studentId || row.userId,
          active: isActive ? 0 : 1,
        });
      } else {
        await updateOrgInstructorActive({
          adminUserId: userId,
          orgId,
          userId: row.userId,
          active: isActive ? 0 : 1,
        });
      }
      clearCacheByPrefix(`admin-users:org:${orgId}:`);
      await loadMembers();
      if (detailOpen && detail?.userId === row.userId) {
        setDetail((prev) => (prev ? { ...prev, active: isActive ? 0 : 1 } : prev));
      }
    } catch (error) {
      setMembersError('Unable to update user access right now.');
    }
  };

  const handleResendInvite = async (row) => {
    if (!guardAction('action.admin.user.invite')) return;
    if (!userId || !orgId || !row?.email) {
      return;
    }
    setMembersError('');
    setListSuccess('');
    try {
      const result = await resendInvite(userId, orgId, row.email);
      if (result.gate) {
        openUpgradeGate(result.body);
        return;
      }
      if (result.ok) {
        clearCacheByPrefix(`admin-users:org:${orgId}:`);
        await loadMembers();
        setListSuccess('Invitation resent successfully.');
        return;
      }
      setMembersError(result.text || 'Unable to resend invitation.');
    } catch (error) {
      setMembersError('Unable to resend invitation. Please try again.');
    }
  };

  const handleRevokeInvite = async (row) => {
    if (!guardAction('action.admin.user.invite')) return;
    if (!userId || !orgId || !row?.inviteId) {
      return;
    }

    const name = row.email || 'this invite';
    const confirmed = await askConfirmation({
      title: 'Revoke invite?',
      message: `${name} will no longer be able to accept this invitation.`,
      confirmLabel: 'Revoke invite',
      variant: 'danger',
      requireTypedConfirm: false,
    });
    if (!confirmed) {
      return;
    }

    setMembersError('');
    setListSuccess('');
    try {
      await revokeInvite(row.inviteId, userId, orgId);
      clearCacheByPrefix(`admin-users:org:${orgId}:`);
      await loadMembers();
      setListSuccess('Invitation revoked.');
    } catch (error) {
      setMembersError('Unable to revoke invitation right now.');
    }
  };

  const sortedRows = useMemo(
    () => sortAdminUsersLocally(members, sortBy, sortDir),
    [members, sortBy, sortDir],
  );

  const isInstructorTab = userKind === ADMIN_USER_KINDS.instructor;
  const emptyText = isInstructorTab
    ? 'No instructors or admins yet. Select Invite user to add your first teammate.'
    : 'No learners enrolled in your organization courses yet.';

  return (
    <Shell>
      <DashboardNavbar menuItems={ADMIN_TABS} />
      <Hero>
        <div>
          <h2>Users &amp; roles</h2>
          <p>Manage instructors, admins, and learners in your organization.</p>
        </div>
        <HeroActions>
          <PageActionsMenu
            primaryLabel="Create user"
            primaryIcon={<Plus size={16} strokeWidth={2.25} aria-hidden="true" />}
            primaryLocked={teamActionsLocked || createDemoLocked}
            onPrimaryClick={() => {
              if (createDemoLocked) {
                guardAction('action.admin.user.create');
                return;
              }
              if (teamActionsLocked) {
                promptTeamBlock();
                return;
              }
              openUserDialog('create');
            }}
            menuTitle="User management"
            menuSubtitle="Create accounts or import a roster file."
          >
            {(closeActions) => (
              <ActionsSection label="Add people">
                <ActionsMenuItem
                  label="Import users"
                  description="Bulk upload from CSV or Excel"
                  icon={<Upload size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  locked={teamActionsLocked || importDemoLocked}
                  onBlockedClick={() => {
                    if (importDemoLocked) guardAction('action.admin.user.import');
                    else promptTeamBlock();
                  }}
                  onClick={() => openUserDialog('import')}
                />
              </ActionsSection>
            )}
          </PageActionsMenu>
        </HeroActions>
      </Hero>

      <Wrap>
        {!isAdmin ? (
          <PlanHint>
            <Tag $tone="gray">View only</Tag>
            <span>{roleBlockedMessage}</span>
          </PlanHint>
        ) : null}

        {isAdmin && !canManageTeam ? (
          <PlanHint $tone="plan">
            <Tag $tone="amber">{INVITE_TEAM_MIN_TIER.charAt(0).toUpperCase() + INVITE_TEAM_MIN_TIER.slice(1)} plan</Tag>
            <span>{planBlockedMessage}</span>
            <Btn $variant="ghost" $sm onClick={() => setShowPlans(true)}>View plans</Btn>
          </PlanHint>
        ) : null}

        {membersError ? <ErrorNote>{membersError}</ErrorNote> : null}
        {listSuccess ? <SuccessNote>{listSuccess}</SuccessNote> : null}

        <SetTabs>
          <SetTab
            $on={isInstructorTab}
            onClick={() => handleKindChange(ADMIN_USER_KINDS.instructor)}
          >
            Instructor users
          </SetTab>
          <SetTab
            $on={!isInstructorTab}
            onClick={() => handleKindChange(ADMIN_USER_KINDS.learner)}
          >
            Learner users
          </SetTab>
        </SetTabs>

        <DataTable
          rows={sortedRows}
          loading={membersLoading}
          rowKey={(member) => member.userId || member.studentId || member.email}
          emptyText={emptyText}
          pageSize={PAGE_SIZE}
          serverSide
          page={listPage + 1}
          totalPages={totalPages}
          totalElements={totalElements}
          onPageChange={handlePageChange}
          search
          searchValue={searchKeyword}
          onSearchChange={setSearchKeyword}
          searchPlaceholder={isInstructorTab
            ? 'Search instructors by name or email…'
            : 'Search learners by name or email…'}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          actions={(member) => (
            <AdminUsersRowActionsMenu
              row={member}
              onToggleStatus={handleToggleStatus}
              onResendInvite={handleResendInvite}
              onRevokeInvite={handleRevokeInvite}
              locked={isRowActionLocked(member)}
              onBlockedClick={() => promptRowBlock(member)}
            />
          )}
          primary={{
            header: 'User',
            sortKey: ADMIN_USERS_SORT_FIELDS.name,
            label: (member) => formatAdminUserName(member),
            sub: (member) => member.email,
            short: (member) => {
              const name = formatAdminUserName(member);
              return name
                ? String(name).replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase()
                : undefined;
            },
          }}
          columns={[
            {
              header: 'Role',
              sortKey: ADMIN_USERS_SORT_FIELDS.role,
              render: (member) => member.role || (isInstructorTab ? 'Instructor' : 'Learner'),
              muted: true,
            },
            ...(isInstructorTab ? [] : [{
              header: 'Enrollments',
              sortKey: ADMIN_USERS_SORT_FIELDS.enrollments,
              render: (member) => member.enrollmentCount ?? 0,
              muted: true,
            }]),
            {
              header: 'Status',
              sortKey: ADMIN_USERS_SORT_FIELDS.status,
              render: (member) => (
                member.status === 'pending' ? (
                  <Tag $tone="amber">
                    <TagDot />
                    Pending
                  </Tag>
                ) : (
                  <Tag $tone={member.active === 1 ? 'green' : 'gray'}>
                    <TagDot />
                    {member.active === 1 ? 'Active' : 'Inactive'}
                  </Tag>
                )
              ),
            },
          ]}
        />
      </Wrap>

      <AdminUserDetailSheet
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => setDetailOpen(false)}
      />

      <PackageDialog open={showPlans} handleClose={() => setShowPlans(false)} />

      {userDialog === 'create' && (
        <ModalShell
          title="Create user"
          sub="Invite by email — they'll get a link to accept."
          max={520}
          onClose={closeUserDialog}
          foot={(
            <ModalFoot>
              <Btn $variant="ghost" onClick={closeUserDialog}>Cancel</Btn>
              <Btn
                disabled={!canCreateUser || createLoading}
                onClick={canCreateUser ? handleCreateSubmit : () => guardTeamAction()}
              >
                {createLoading ? 'Sending…' : 'Send invite'}
              </Btn>
            </ModalFoot>
          )}
        >
          <FormGrid>
            <Field>
              <label htmlFor="create-first-name">First name</label>
              <Input
                id="create-first-name"
                placeholder="e.g. Jane"
                value={createFirstName}
                onChange={(e) => setCreateFirstName(e.target.value)}
                disabled={!canCreateUser || createLoading}
              />
            </Field>
            <Field>
              <label htmlFor="create-last-name">Last name</label>
              <Input
                id="create-last-name"
                placeholder="e.g. Doe"
                value={createLastName}
                onChange={(e) => setCreateLastName(e.target.value)}
                disabled={!canCreateUser || createLoading}
              />
            </Field>
            <Field>
              <label htmlFor="create-email">Email</label>
              <Input
                id="create-email"
                placeholder="name@company.com"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                disabled={!canCreateUser || createLoading}
              />
            </Field>
          </FormGrid>
          <Field>
            <label htmlFor="create-role">Role</label>
            <Select
              id="create-role"
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value)}
              disabled={!canCreateUser || createLoading}
            >
              {createRoleOptions.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </Select>
          </Field>
          {createError ? <ErrorNote>{createError}</ErrorNote> : null}
        </ModalShell>
      )}

      {userDialog === 'import' && (
        <ModalShell
          title="Import users"
          sub="Bulk-import from CSV or Excel."
          max={520}
          onClose={closeUserDialog}
          foot={(
            <ModalFoot>
              <Btn $variant="ghost" onClick={closeUserDialog}>Cancel</Btn>
              <Btn
                $variant="ghost"
                disabled={!canImportUsers}
                onClick={() => {
                  if (!guardAction('action.admin.user.import')) return;
                  if (!canImportUsers) guardTeamAction();
                }}
              >
                Upload &amp; import
              </Btn>
            </ModalFoot>
          )}
        >
          <Field>
            <label htmlFor="import-file">File</label>
            <FileInput id="import-file" type="file" disabled={!canImportUsers} />
          </Field>
          {canImportUsers ? <ApiNote /> : null}
        </ModalShell>
      )}
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </Shell>
  );
}
